import React, { ReactNode, useEffect, useRef, useState } from 'react';
import differenceBy from 'lodash.differenceby';
import isEqual from 'lodash.isequal';
import { AnyLayer, Layer, GeoJSONSource, Map as MapBoxMap, MapboxGeoJSONFeature } from 'mapbox-gl';

import { layerNames, mapboxLayerTypes } from './constants';

const mapSourceId = 'wbGeoJsonSource';

interface GeoJsonLayerPoint {
    type: 'Point';
    coordinates: [number, number];
}

interface GeoJsonLayerType {
    map: InstanceType<typeof MapBoxMap>;
    geojson: GeoJSON.FeatureCollection;
    layers: Layer[];
    panOnSelection: boolean;
    featureSelected: (feature: MapboxGeoJSONFeature, map?: MapBoxMap) => void;
    featureIcons: Record<string, string>;
    requirePointId: boolean;
    children: ReactNode;
}

export const GeoJsonLayer = ({
    map,
    geojson,
    layers,
    panOnSelection,
    featureSelected,
    featureIcons,
    requirePointId = true,
    children
}: GeoJsonLayerType) => {
    const [ currentLayers, setCurrentLayers] = useState(layers);
    const previousGeojson = useRef<GeoJSON.FeatureCollection | undefined>();
    const imagesLoaded = useRef<boolean>(false);
    const featuresLoaded = useRef<boolean>(false);

    const addIconImages = async(featureIcons: Record<string, string>) => {
        if (featureIcons) { 
            const promises: Promise<void>[] = [];
            const icons = Object.keys(featureIcons);
            icons?.forEach(icon => {
                promises.push(new Promise(resolve => {
                    map.loadImage(featureIcons[icon], (error, image)=>{
                        if (error) {
                            return;
                        }
                        if (image) {
                            map.addImage(icon, image);
                        }
                        resolve();
                    });
                }));
            });
            try {
                await Promise.all(promises);
                imagesLoaded.current = true;
            } catch (error) {
                console.log('error: ', error);
            }
        }
    };

    const selectFeature = (ev: mapboxgl.MapLayerMouseEvent) => {
        const feature = requirePointId ? ev.features?.find((x) => x.properties?.id.toLowerCase().includes('point')) : ev.features?.[0];

        if (feature && map) {
            featureSelected?.(feature, map);

            if(panOnSelection && feature?.geometry) {
                const pointGeometry = feature.geometry as GeoJsonLayerPoint;
                const coordinates = pointGeometry.coordinates;
                map.easeTo({ center: coordinates });
            }
        }
    };

    const addSourceAndLayer = async(map: InstanceType<typeof MapBoxMap>, featureIcons: Record<string, string>, geojson: GeoJSON.FeatureCollection, layers: Layer[]) => {
        if (!imagesLoaded.current && featureIcons) {
            await addIconImages(featureIcons);
        }
        if (map) {
            if (geojson) {
                map.addSource(mapSourceId, {
                    type: 'geojson',
                    data: geojson,
                    cluster: false,
                    generateId: true // Ensure all layer features have unique ID - to correctly implement setFeatureState
                });   
            }
    
            const addLayers = () => {
                if (layers?.length) {
                    layers?.forEach((layer) => {
                        layer.source = mapSourceId;
                        map.addLayer(layer as AnyLayer);
                        if (layer.type && mapboxLayerTypes.includes(layer.type.toLowerCase())) {
                            // Change the cursor to a pointer when the mouse is over the layer.
                            map.on('mouseenter', layer.id, function () {
                                if (map?.getCanvas?.()?.style?.cursor) {
                                    map.getCanvas().style.cursor = 'pointer';
                                }
                            });
        
                            // Change it back to a pointer when it leaves.
                            map.on('mouseleave', layer.id, function () {
                                if (map?.getCanvas?.()?.style?.cursor) {
                                    map.getCanvas().style.cursor = '';
                                }
                            });
                            map.on('click', layer.id, selectFeature);
                        }
                    });
                }
            };
    
            const onStyleData = () => {
                if(map.isStyleLoaded()) {
                    // code executes once style has loaded
                    addLayers();
                    map.off('data', onStyleData);
                }
            };
            //keep listening to the data event until the style is loaded
            map.on('data', onStyleData);
        }
        
    };

    const tryAddLayers = (map: InstanceType<typeof MapBoxMap>, geojson: GeoJSON.FeatureCollection, featureIcons: Record<string, string>, layers: Layer[]) => {
        if (!featuresLoaded.current && map && geojson?.features?.length > 0) {
            if (map?.loaded()) {
                addSourceAndLayer(map, featureIcons, geojson, layers);
            } else {
                map?.on('load', () => {
                    addSourceAndLayer(map, featureIcons, geojson, layers);
                });
            }
            featuresLoaded.current = true;
        }
    };

    useEffect(() => {
        if (map && geojson && layers?.length) {
            if (featuresLoaded.current && !isEqual(geojson, previousGeojson.current)) {
                previousGeojson.current = geojson;
                const mapSource: GeoJSONSource = map.getSource(mapSourceId) as GeoJSONSource;
                if (mapSource) {
                    mapSource.setData(geojson);
                }
            }

            const layerDifferences = differenceBy(layers, currentLayers, 'filter');
            if (featuresLoaded.current && layerDifferences.length > 0) {
                setCurrentLayers(layers);

                layerDifferences.forEach(layer => {
                    if (layer.id && layer?.filter) {
                        // Check if layer exists in the map's style, cannot be filtered if it doesn't.
                        const mapLayer = map.getLayer(layer.id);
                        if (mapLayer) {
                            map.setFilter(layer?.id, layer?.filter);
                        }
                    }
                });
            }
            tryAddLayers(map, geojson, featureIcons, layers);
        }
    }, [map, geojson, featureIcons, previousGeojson.current, layers, currentLayers, featuresLoaded]);

    return (
        <div>{children}</div>
    );
};
